import React, { useState } from "react";
import { Info, Phone } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import defaultImg from "../Images/spring.png";
import img from "../Images/pilot.avif";
import img1 from "../Images/thermal.png";
import img2 from "../Images/springsafetyspec.png";
import img3 from "../Images/thermalspec.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article 
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6 lg:p-8"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-gray-900" itemProp="name">
          {product.name}
        </h3>
      </header>
      
      <div className="bg-gray-100 h-48 sm:h-56 md:h-64 lg:h-72 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - High-performance industrial safety valve by Gortherm Energy`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
          width="600"
          height="400"
        />
      </div>

      <nav className="mb-4">
        <div className="flex border-b border-gray-200" role="tablist" aria-label="Product information">
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'description'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('description')}
            aria-selected={activeTab === 'description'}
            role="tab"
            aria-controls="description-panel"
          >
            Description
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'specs'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('specs')}
            aria-selected={activeTab === 'specs'}
            role="tab"
            aria-controls="specs-panel"
          >
            <span className="hidden sm:inline">Technical Specifications</span>
            <span className="sm:hidden">Specs</span>
          </button>
        </div>
      </nav>

      <div className="mt-4" role="tabpanel" id={activeTab === 'description' ? 'description-panel' : 'specs-panel'}>
        {activeTab === 'description' ? (
          <p className="text-gray-700 text-justify text-sm sm:text-base leading-relaxed" itemProp="description">
            {product.description}
          </p>
        ) : (
          <div>
            {product.specImage && (
              <div className="bg-gray-100 h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} detailed technical specifications and measurements`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            )}
            <ul className="space-y-2 sm:space-y-3">
              {product.specifications.map((spec, index) => (
                <li key={index} className="flex items-start">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700 text-sm sm:text-base">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
};

const SafetyValves = () => {
  const handleCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const products = [
    {
      id: "spring-loaded",
      name: "Spring-Loaded Safety Valve",
      mainImage: defaultImg,
      specImage: img2,
      description: `Gortherm Energy's Spring-Loaded Safety Valves are engineered to provide reliable overpressure protection in critical applications. These valves feature a precision-engineered spring mechanism that maintains consistent set pressure and ensures rapid response to overpressure conditions. The design incorporates high-quality materials and advanced manufacturing techniques to deliver exceptional reliability and performance. Our spring-loaded safety valves are ideal for steam, gas, and liquid applications where precise pressure control and immediate relief are essential for system safety.`,
      specifications: [
        'Precise pressure control with adjustable settings',
        'Quick response to overpressure conditions',
        'ASME certified design and construction',
        'Multiple spring ranges available',
        'Pop-action for immediate pressure relief',
        'High flow capacity design'
      ]
    },
    {
      id: "pilot-operated",
      name: "Pilot-Operated Safety Valve",
      mainImage: img,
      specImage: "/ProdSpec/PO_Spec.jpg",
      description: `Our Pilot-Operated Safety Valves represent the pinnacle of pressure relief technology. These valves utilize a pilot mechanism to control the main valve operation, providing exceptional accuracy and performance in demanding applications. The sophisticated design allows for nearly full capacity operation at set pressure, minimizing product loss and improving system efficiency. Gortherm Energy manufactures these valves with premium materials and advanced engineering to ensure long-term reliability in critical safety applications.`,
      specifications: [
        'High precision pressure control',
        'Bubble-tight sealing up to set pressure',
        'Modular pilot design for easy maintenance',
        'Balanced bellows construction',
        'Suitable for back pressure applications',
        'Remote pressure sensing capability'
      ]
    },
    {
      id: "thermal-relief",
      name: "Thermal Relief Valve",
      mainImage: img1,
      specImage: img3,
      description: `Thermal Relief Valves from Gortherm Energy are specifically designed to protect systems from overpressure caused by thermal expansion. These compact yet powerful valves provide crucial protection for isolated liquid-filled systems where thermal expansion can create dangerous pressure buildup. Our thermal relief valves feature precision-machined components and carefully selected materials to ensure reliable operation in various temperature conditions. The design emphasizes simplicity and reliability while maintaining the highest safety standards.`,
      specifications: [
        'Compact design for space-restricted installations',
        'Temperature-compensated operation',
        'Self-closing mechanism',
        'Low maintenance requirements',
        'Various pressure settings available',
        'Suitable for multiple fluid types'
      ]
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Safety Valves & Pressure Relief Solutions",
    "description": "Industrial safety valves including spring-loaded, pilot-operated, and thermal relief valves for critical pressure protection",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.mainImage,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Safety Valves & Pressure Relief Systems | Spring-Loaded, Pilot-Operated | Gortherm Energy</title>
        <meta name="description" content="Industrial safety valves and pressure relief systems including spring-loaded safety valves, pilot-operated safety valves, and thermal relief valves. ASME certified overpressure protection for critical applications." />
        <meta name="keywords" content="safety valves, pressure relief valves, spring-loaded safety valve, pilot-operated safety valve, thermal relief valve, overpressure protection, ASME safety valves, industrial safety valves, pressure relief systems Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Safety Valves & Pressure Relief Systems | Gortherm Energy" />
        <meta property="og:description" content="High-performance safety valves for industrial overpressure protection. Spring-loaded, pilot-operated, and thermal relief valve solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/safety-valves" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Safety Valves & Pressure Relief Systems | Gortherm Energy" />
        <meta name="twitter:description" content="ASME certified safety valves for critical industrial applications and pressure protection." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/safety-valves" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Safety Valves & Pressure Relief Solutions
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Discover our comprehensive range of safety valves and pressure relief solutions,
              engineered to meet the demanding requirements of modern industrial processes.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <aside className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-4">
                Our technical experts are ready to help you select the right safety valve 
                for your specific application and ensure optimal pressure protection.
              </p>
              <button
                onClick={handleCall}
                className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold"
                aria-label="Request consultation - Call Gortherm Energy"
              >
                <span>Request Consultation</span>
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              </button>
             
            </div>
          </aside>

          {/* Additional SEO Content Section */}
          <section className="mt-12 sm:mt-16 bg-white rounded-xl p-6 sm:p-8 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Why Choose Gortherm Energy Safety Valves?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">ASME Certified</h3>
                <p className="text-sm sm:text-base">
                  Our safety valves meet or exceed ASME standards, ensuring compliance with international 
                  safety regulations and providing reliable overpressure protection.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Precision Engineering</h3>
                <p className="text-sm sm:text-base">
                  Manufactured with precision-machined components and high-quality materials for 
                  accurate pressure control and consistent performance in critical applications.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Versatile Applications</h3>
                <p className="text-sm sm:text-base">
                  Suitable for steam, gas, liquid, and thermal expansion applications across diverse 
                  industries including chemical, petrochemical, power generation, and manufacturing.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Low Maintenance</h3>
                <p className="text-sm sm:text-base">
                  Robust construction and quality components minimize maintenance requirements while 
                  ensuring long service life and reliable operation in demanding environments.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default SafetyValves;